import React from 'react';
import { useStudioTypeSize } from '../StudioTypeScale';
import type { StudioPageContent } from '../../../constants';
import { resolveComparisonContent } from '../../../utils/contentCopy';
import LayoutFrame, { type LayoutContentProps } from './LayoutFrame';

interface ComparisonContentProps extends Partial<LayoutContentProps> {
  content: StudioPageContent;
  format: string;
  locale?: string;
}

const Column: React.FC<{
  title: string;
  items: string[];
  color: string;
  positive: boolean;
  isStory: boolean;
  t: any;
}> = ({ title, items, color, positive, isStory, t }) => {
  const typeSize = useStudioTypeSize();
  return (
  <div
    style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: isStory ? 11 : 8,
      padding: isStory ? '16px 14px' : '13px 12px',
      borderRadius: t.radius.lg,
      background: t.cardBg,
      border: `1px solid ${t.hairline}`,
      boxShadow: t.shadow
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        fontSize: typeSize(isStory ? 13 : 11.5),
        fontWeight: 700,
        color,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 2
      }}
    >
      <span
        style={{
          width: 20,
          height: 20,
          borderRadius: t.radius.pill,
          background: color,
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
      >
        {positive ? (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        )}
      </span>
      {title}
    </div>
    {items.map((item, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 7,
          fontSize: typeSize(isStory ? 11.5 : 10),
          fontWeight: 500,
          color: t.ink,
          lineHeight: 1.4
        }}
      >
        <span style={{ color, fontWeight: 800, lineHeight: 1.4 }}>{positive ? '＋' : '－'}</span>
        <span>{item}</span>
      </div>
    ))}
  </div>
  );
};

const ComparisonContent: React.FC<ComparisonContentProps> = ({ content, format, locale, tokens }) => {
  const isStory = format === 'story';
  const t = tokens!;
  const typeSize = useStudioTypeSize();
  const resolved = resolveComparisonContent(content, locale);

  return (
    <div>
      <div
        style={{
          maxWidth: 306,
          color: t.ink,
          fontSize: typeSize(isStory ? 28 : 23),
          fontWeight: 700,
          letterSpacing: '-0.8px',
          lineHeight: 1.18,
          marginBottom: isStory ? 24 : 17
        }}
      >
        {resolved.title}
      </div>
      <div style={{ display: 'flex', gap: 11 }}>
        <Column
          title={resolved.leftTitle}
          items={resolved.leftItems}
          color={t.neg}
          positive={false}
          isStory={isStory}
          t={t}
        />
        <Column
          title={resolved.rightTitle}
          items={resolved.rightItems}
          color={t.pos}
          positive
          isStory={isStory}
          t={t}
        />
      </div>
    </div>
  );
};

interface ComparisonLayoutProps {
  width: number;
  height: number;
  format: string;
  locale?: string;
  content?: StudioPageContent;
  themeColor?: string;
  profileName?: string;
}

const ComparisonLayout: React.FC<ComparisonLayoutProps> = props => (
  <LayoutFrame {...props}>
    <ComparisonContent content={props.content || {}} format={props.format} locale={props.locale} />
  </LayoutFrame>
);

export default ComparisonLayout;
